import type { CategoryConfig, CmpConfig, CookieConfig } from "./config";
import type { ConsentState } from "./consent";

const DEFAULT_LOGIN_RECONSENT: Record<string, string> = {
  tr: "Giriş yaptığınız için gizlilik tercihlerinizin güncellenmesi gerekiyor.",
  en: "Since you have logged in, your privacy preferences need to be updated.",
};

function parseThemeRecord(themeRaw: unknown): Record<string, unknown> | null {
  if (!themeRaw || typeof themeRaw !== "object") return null;
  return themeRaw as Record<string, unknown>;
}

/**
 * Admin themeJson içinde `loginReconsentNotice`, `loginReconsentNoticeJson` (dil → metin) desteklenir.
 * Yoksa tarayıcı diline göre tr / diğer tümü → en.
 */
export function resolveLoginReconsentNotice(config: CmpConfig): string {
  const theme = parseThemeRecord(config.themeJson);
  if (theme) {
    const jsonRaw = theme.loginReconsentNoticeJson;
    if (jsonRaw && typeof jsonRaw === "string") {
      try {
        const parsed = JSON.parse(jsonRaw) as Record<string, string>;
        const fromJson = pickLocalizedString(parsed);
        if (fromJson) return fromJson;
      } catch {
        /* ignore */
      }
    } else if (jsonRaw && typeof jsonRaw === "object") {
      const fromJson = pickLocalizedString(jsonRaw as Record<string, string>);
      if (fromJson) return fromJson;
    }
    const single = theme.loginReconsentNotice;
    if (typeof single === "string" && single.trim()) return single.trim();
  }

  const nav = (typeof navigator !== "undefined" ? navigator.language : "en").toLowerCase();
  const short = nav.split("-")[0] || "en";
  if (short === "tr") return DEFAULT_LOGIN_RECONSENT.tr;
  return DEFAULT_LOGIN_RECONSENT.en;
}

function pickLocalizedString(map: Record<string, string>): string | null {
  const nav = (typeof navigator !== "undefined" ? navigator.language : "en").toLowerCase();
  const short = nav.split("-")[0] || "en";
  if (map[nav]?.trim()) return map[nav].trim();
  if (map[short]?.trim()) return map[short].trim();
  if (map.en?.trim()) return map.en.trim();
  if (map.tr?.trim()) return map.tr.trim();
  const first = Object.values(map).find((v) => typeof v === "string" && v.trim());
  return first?.trim() ?? null;
}

export function listBrowserCookieNames(): string[] {
  try {
    if (!document.cookie) return [];
    return document.cookie
      .split(";")
      .map((p) => p.split("=")[0]?.trim())
      .filter((n): n is string => !!n && n.length > 0);
  } catch {
    return [];
  }
}

/**
 * Tarayıcıdaki çerezleri mevcut rıza ile karşılaştırır.
 * Katalogda olmayan çerez veya reddedilmiş kategorideki çerez → true (yeniden rıza gerekir).
 * Katalog boşsa kontrol yapılmaz (false).
 */
export function detectPostLoginCookieMismatch(
  consent: ConsentState | null,
  cookieCatalog: CookieConfig[] | undefined,
  categories: CategoryConfig[] | undefined
): boolean {
  if (!consent || !cookieCatalog?.length) return false;

  const requiredCatKeys = new Set((categories || []).filter((c) => c.required).map((c) => c.key));

  const names = listBrowserCookieNames();
  if (names.length === 0) return false;

  const byLowerName = new Map<string, CookieConfig>();
  for (const c of cookieCatalog) {
    byLowerName.set(c.cookieName.toLowerCase(), c);
  }

  for (const name of names) {
    const entry = byLowerName.get(name.toLowerCase());
    if (!entry) {
      return true;
    }

    const cat = (entry.category || "unclassified").toLowerCase();
    if (requiredCatKeys.has(cat)) continue;

    const catCfg = (categories || []).find((x) => x.key === cat);
    if (catCfg?.required) continue;

    const allowed = consent.categories[cat] ?? false;
    if (!allowed) return true;
  }

  return false;
}
